import AssignmentIcon from "@mui/icons-material/Assignment";
import GroupIcon from "@mui/icons-material/Group";
import { Grid, Paper, styled, Typography } from "@mui/material";
import { api, types } from "api";
import crown from "assets/icons/crown.svg";
import { AssignmentModelList } from "components/AssignmentModelList";
import { GroupList } from "components/GroupList/GroupList";
import { InfoSnackBar } from "components/InfoSnackBar";
import { Assignment, getStatus } from "helpers/assignment";
import { ACTIVITIES_FETCH_ERR } from "messages";
import { useEffect, useRef, useState } from "react";

const PREFIX = "StudentHomeDashboardPage";

const classes = {
	root: `${PREFIX}-root`,
	button: `${PREFIX}-button`,
	title: `${PREFIX}-title`,
	groupList: `${PREFIX}-groupList`,
	summaryCard: `${PREFIX}-summaryCard`,
	summaryTitle: `${PREFIX}-summaryTitle`,
	summaryText: `${PREFIX}-summaryText`,
	icon: `${PREFIX}-icon`,
};

const Root = styled("div")(({ theme }) => ({
	[`&.${classes.root}`]: {
		padding: 20,
	},

	[`& .${classes.button}`]: {
		marginTop: 15,
		marginRight: 5,
		float: "right",
	},

	[`& .${classes.title}`]: {
		marginTop: 5,
		marginBottom: 15,
		float: "left",
	},

	[`& .${classes.groupList}`]: {
		maxHeight: 200,
		padding: 5,
		overflowY: "auto",
		overflowX: "hidden",
		float: "left",
	},

	[`& .${classes.summaryCard}`]: {
		marginTop: 30,
		height: 200,
		padding: theme.spacing(2),
		minWidth: 200,
		marginRight: theme.spacing(4),
	},

	[`& .${classes.summaryTitle}`]: {
		color: theme.palette.text.secondary,
		textAlign: "center",
		fontSize: 16,
		paddingTop: 15,
		marginBottom: 25,
	},

	[`& .${classes.summaryText}`]: {
		fontSize: 16,
		textAlign: "center",
		fontWeight: "bold",
		marginTop: 15,
		marginBottom: 25,
	},

	[`& .${classes.icon}`]: {
		color: theme.palette.warning.main,
	},
}));

export const StudentHomeDashboardPage = (): JSX.Element => {
	const [assignments, setAssignment] = useState<Assignment[]>([]);
	const [groups, setGroups] = useState<types.Group[]>([]);
	const [err, setErr] = useState(false);
	const [name, setName] = useState("");
	const [role, setRole] = useState("");
	const [groupsNum, setGroupsNum] = useState(0);

	const componentMounted = useRef(true);

	const showRole = (roles: types.Roles): void => {
		if (roles.includes("student")) {
			setRole("Student");
			return;
		}
		if (roles.includes("parent")) {
			setRole("Parent");
			return;
		}
	};

	useEffect(() => {
		api
			.getMyUserData()
			.then((res) => {
				if (res.status !== 200) {
					setErr(true);
					return;
				}

				if (componentMounted.current) {
					setName(res.data.firstName);
					showRole(res.data.role);
				}
			})
			.catch(() => {
				setErr(true);
			});
		api
			.getAssignments()
			.then((res) => {
				if (res.status !== 200) {
					setErr(true);
					return;
				}

				if (componentMounted.current) {
					const now = new Date();
					setAssignment(
						(res.data || []).map((ass) => ({
							...ass,
							status: getStatus(ass, now),
						})),
					);
				}
			})
			.catch(() => {
				setErr(true);
			});
		api
			.getGroups()
			.then((res) => {
				if (res.status !== 200) {
					setErr(true);
					return;
				}

				if (componentMounted.current) {
					setGroups(res.data || []);
					if (res.data !== null) {
						setGroupsNum(res.data.length);
					}
				}
			})
			.catch(() => {
				setErr(true);
			});
		return () => {
			componentMounted.current = false;
		};
	}, []);

	return (
		<Root className={classes.root}>
			<Grid container spacing={2}>
				<Grid item xs={12}>
					<Typography variant="h2" data-testid="hello">
						Hi {name} 👋
					</Typography>
					<Typography variant="h5">
						Welcome back to Science Island. Here’s a summary of what you’ve
						missed!
					</Typography>
				</Grid>
				<Grid item xs={5}>
					<Grid container spacing={2}>
						<Paper className={classes.summaryCard}>
							<Typography className={classes.summaryTitle}>ROLE</Typography>
							<Typography className={classes.summaryText}>{role}</Typography>
							<Grid container justifyContent="space-around">
								<img
									data-testid="crown"
									src={crown}
									alt="crown"
									style={{ maxHeight: 60, maxWidth: 60 }}
								/>
							</Grid>
						</Paper>
						<Paper className={classes.summaryCard}>
							<Typography className={classes.summaryTitle}>
								TOTAL GROUPS
							</Typography>
							<Typography className={classes.summaryText}>
								{groupsNum} groups
							</Typography>
							<Grid container justifyContent="space-around">
								<GroupIcon
									data-testid="groups"
									fontSize="large"
									className={classes.icon}
								/>
							</Grid>
						</Paper>
						<Paper className={classes.summaryCard}>
							<Typography className={classes.summaryTitle}>
								TOTAL ASSIGNMENTS
							</Typography>
							<Typography className={classes.summaryText}>
								{assignments.length} assignments
							</Typography>
							<Grid container justifyContent="space-around">
								<AssignmentIcon
									data-testid="assignments"
									fontSize="large"
									className={classes.icon}
								/>
							</Grid>
						</Paper>
					</Grid>
				</Grid>
				<Grid item xs={7}>
					<Grid container spacing={1}>
						<Grid item xs={12}>
							<Typography className={classes.title} variant="h4">
								Groups
							</Typography>
						</Grid>
						<Grid item xs={12} className={classes.groupList}>
							{groups.length === 0 ? (
								<Typography variant="h5" className={classes.title}>
									No groups
								</Typography>
							) : (
								<GroupList groups={groups} />
							)}
						</Grid>
					</Grid>
				</Grid>

				<Grid item xs={12}>
					<Typography className={classes.title} variant="h4">
						Assignments
					</Typography>
				</Grid>

				<Grid item xs={12}>
					{assignments.length === 0 ? (
						<Typography variant="h5" className={classes.title}>
							No ongoing assignments
						</Typography>
					) : (
						<AssignmentModelList assignments={assignments} show_csv={false} />
					)}
				</Grid>
			</Grid>

			<InfoSnackBar
				isOpen={err}
				onClose={() => setErr(false)}
				message={ACTIVITIES_FETCH_ERR}
				severity="error"
			/>
		</Root>
	);
};
